import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { File, FileImage, FileText, UploadCloud, X } from "lucide-react";

import FormField from "./FormField";
import IconButton from "./IconButton";

export const formatFileSize = (bytes) => {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 bytes";
  const units = ["bytes", "KB", "MB", "GB"];
  const unitIndex = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / (1024 ** unitIndex);
  return `${value.toFixed(unitIndex === 0 || value >= 10 ? 0 : 1)} ${units[unitIndex]}`;
};

const matchesAccept = (file, accept) => {
  if (!accept) return true;
  const rules = accept.split(",").map((rule) => rule.trim().toLowerCase()).filter(Boolean);
  const fileName = file.name.toLowerCase();
  const fileType = (file.type || "").toLowerCase();
  return rules.some((rule) => {
    if (rule.startsWith(".")) return fileName.endsWith(rule);
    if (rule.endsWith("/*")) return fileType.startsWith(rule.slice(0, -1));
    return fileType === rule;
  });
};

const getFileKey = (file) => file.id || `${file.name}-${file.size}-${file.lastModified || "existing"}`;
const checksumCache = new WeakMap();

const getFileChecksum = async (file) => {
  if (file.checksum) return file.checksum;
  if (!(file instanceof Blob) || !window.crypto?.subtle) return null;
  if (!checksumCache.has(file)) {
    checksumCache.set(file, window.crypto.subtle.digest("SHA-256", await file.arrayBuffer())
      .then((digest) => Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join("")));
  }
  return checksumCache.get(file);
};

const hasPdfSignature = async (file) => {
  if (!(file instanceof Blob)) return true;
  const bytes = new Uint8Array(await file.slice(0, 5).arrayBuffer());
  return String.fromCharCode(...bytes) === "%PDF-";
};

export const formatUploadDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    hour: "2-digit",
    hour12: false,
    minute: "2-digit",
    month: "short",
    second: "2-digit",
    fractionalSecondDigits: 3,
    timeZone: "Europe/London",
    year: "numeric",
  }).format(date);
};

const summariseNames = (files) => {
  const names = files.slice(0, 2).map((file) => file.name);
  if (files.length > 2) names.push(`${files.length - 2} other${files.length === 3 ? "" : "s"}`);
  return names.length > 1 ? `${names.slice(0, -1).join(", ")} and ${names.at(-1)}` : names[0];
};

const FileField = ({
  accept,
  disabled = false,
  error,
  files,
  helpText,
  id,
  label,
  maxFileSizeBytes,
  maxFiles,
  maxTotalSizeBytes,
  multiple = false,
  onChange,
  onOpenFile,
  onReject,
  required = false,
}) => {
  const generatedId = useId();
  const inputId = id || generatedId;
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [validationError, setValidationError] = useState("");


  const limits = [
    maxFileSizeBytes && `${formatFileSize(maxFileSizeBytes)} per file`,
    multiple && maxTotalSizeBytes && `${formatFileSize(maxTotalSizeBytes)} total`,
    multiple && maxFiles && `${maxFiles} ${maxFiles === 1 ? "file" : "files"} maximum`,
  ].filter(Boolean).join(" \u00b7 ");

  const addFiles = async (fileList) => {
    const incoming = Array.from(fileList || []);
    if (!incoming.length) return;

    const accepted = multiple ? [...files] : [];
    const messages = [];
    const overCount = [];
    const existingKeys = new Set(accepted.map(getFileKey));
    const existingChecksums = new Set(accepted.map((file) => file.checksum).filter(Boolean));

    for (const file of incoming) {
      if (!matchesAccept(file, accept)) {
        messages.push(`${file.name}: file type is not accepted.`);
        continue;
      }
      if (file.size === 0) {
        messages.push(`${file.name}: empty files cannot be added.`);
        continue;
      }
      if ((accept || "").toLowerCase().includes("pdf") && !(await hasPdfSignature(file))) {
        messages.push(`${file.name}: the file is not a valid PDF.`);
        continue;
      }
      if (maxFileSizeBytes && file.size > maxFileSizeBytes) {
        messages.push(`${file.name}: exceeds the ${formatFileSize(maxFileSizeBytes)} per-file limit.`);
        continue;
      }
      if (existingKeys.has(getFileKey(file))) {
        messages.push(`${file.name}: this file has already been added.`);
        continue;
      }
      const checksum = await getFileChecksum(file);
      if (checksum && existingChecksums.has(checksum)) {
        messages.push(`${file.name}: this file has already been added.`);
        continue;
      }
      if (multiple && maxFiles && accepted.length >= maxFiles) {
        overCount.push(file);
        continue;
      }
      const nextTotal = accepted.reduce((sum, current) => sum + (current.size || 0), 0) + file.size;
      if (multiple && maxTotalSizeBytes && nextTotal > maxTotalSizeBytes) {
        messages.push(`${file.name}: would exceed the ${formatFileSize(maxTotalSizeBytes)} total limit.`);
        continue;
      }
      accepted.push(file);
      existingKeys.add(getFileKey(file));
      if (checksum) existingChecksums.add(checksum);
    }

    if (overCount.length) {
      messages.push(`${summariseNames(overCount)} ${overCount.length === 1 ? "was" : "were"} not added. A maximum of ${maxFiles} ${maxFiles === 1 ? "file" : "files"} can be added.`);
    }

    const nextFiles = multiple ? accepted : accepted.slice(-1);
    if (nextFiles.length !== files.length || nextFiles.some((item, index) => item !== files[index])) onChange(nextFiles);
    const message = messages.join(" ");
    setValidationError(message);
    if (messages.length && onReject) onReject(messages);
    if (inputRef.current) inputRef.current.value = "";
  };

  const removeFile = (index) => {
    onChange(files.filter((_, fileIndex) => fileIndex !== index));
    setValidationError("");
  };

  const currentError = error || validationError;

  return (
    <>
    <FormField error={currentError} helpText={helpText} id={inputId} label={label} required={required}>
      {(fieldProps) => (
        <>
          <input
            {...fieldProps}
            accept={accept}
            className="wsm-file-field__input"
            disabled={disabled}
            multiple={multiple}
            name={inputId}
            onChange={(event) => addFiles(event.target.files)}
            ref={inputRef}
            type="file"
          />
          <div
            className={["wsm-file-field", dragging && "wsm-file-field--dragging", disabled && "wsm-file-field--disabled"].filter(Boolean).join(" ")}
            onDragEnter={(event) => { event.preventDefault(); if (!disabled) setDragging(true); }}
            onDragLeave={(event) => { event.preventDefault(); setDragging(false); }}
            onDragOver={(event) => event.preventDefault()}
            onDrop={(event) => { event.preventDefault(); setDragging(false); if (!disabled) addFiles(event.dataTransfer.files); }}
          >
            <UploadCloud aria-hidden="true" className="wsm-file-field__upload-icon" />
            <div className="wsm-file-field__prompt">
              <strong>{multiple ? "Drop files here" : "Drop a file here"}</strong>
              <span>or</span>
              <button disabled={disabled} onClick={() => inputRef.current?.click()} type="button">Browse files</button>
            </div>
            {limits && <p className="wsm-file-field__limits">{limits}</p>}
          </div>
        </>
      )}
    </FormField>
    <FilePreviewList disabled={disabled} files={files} onOpenFile={onOpenFile} onRemove={removeFile} />
    </>
  );
};

FileField.propTypes = {
  accept: PropTypes.string,
  disabled: PropTypes.bool,
  error: PropTypes.string,
  files: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    created_at: PropTypes.string,
    checksum: PropTypes.string,
    lastModified: PropTypes.number,
    name: PropTypes.string.isRequired,
    previewUrl: PropTypes.string,
    size: PropTypes.number.isRequired,
    type: PropTypes.string,
    updated_at: PropTypes.string,
    uploaded_at: PropTypes.string,
  })).isRequired,
  helpText: PropTypes.string,
  id: PropTypes.string,
  label: PropTypes.string.isRequired,
  maxFileSizeBytes: PropTypes.number,
  maxFiles: PropTypes.number,
  maxTotalSizeBytes: PropTypes.number,
  multiple: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  onOpenFile: PropTypes.func,
  onReject: PropTypes.func,
  required: PropTypes.bool,
};

const FilePreviewList = ({ disabled = false, files, onOpenFile, onRemove }) => {
  const previews = useMemo(() => files.map((file) => ({
    file,
    key: getFileKey(file),
    url: file.previewUrl || (file instanceof Blob ? URL.createObjectURL(file) : undefined),
  })), [files]);

  useEffect(() => () => {
    previews.forEach((preview) => {
      if (!preview.file.previewUrl && preview.url) URL.revokeObjectURL(preview.url);
    });
  }, [previews]);

  if (!files.length) return null;

  return (
    <ul className="wsm-file-list" aria-label="Selected files">
      {previews.map(({ file, key, url }, index) => {
        const isImage = (file.type || "").startsWith("image/") && url;
        const isPdf = file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");
        const TypeIcon = isImage ? FileImage : isPdf ? FileText : File;
        const uploadDate = formatUploadDate(file.uploaded_at || file.created_at || file.updated_at);
        return (
          <li className="wsm-file-list__item" key={key}>
            <div className="wsm-file-list__preview">
              {isImage ? <img alt="" src={url} /> : <TypeIcon aria-hidden="true" />}
            </div>
            <div className="wsm-file-list__details">
              {url ? <a href={url} rel="noreferrer" target="_blank">{file.name}</a> : onOpenFile ? <button className="wsm-file-list__open" onClick={() => onOpenFile(file)} type="button">{file.name}</button> : <strong>{file.name}</strong>}
              <span className="wsm-file-list__metadata">
                <span>{formatFileSize(file.size)}</span>
                <span>{uploadDate ? `Uploaded ${uploadDate}` : file instanceof Blob ? "Saving\u2026" : "Upload date unavailable"}</span>
              </span>
            </div>
            <IconButton aria-label={`Remove ${file.name}`} disabled={disabled} icon={X} onClick={() => onRemove(index)} size="compact" />
          </li>
        );
      })}
    </ul>
  );
};

FilePreviewList.propTypes = {
  disabled: PropTypes.bool,
  files: FileField.propTypes.files,
  onOpenFile: PropTypes.func,
  onRemove: PropTypes.func.isRequired,
};

export { FilePreviewList };
export default FileField;
